import React, { useState, useEffect } from "react";
import { FiSearch } from "react-icons/fi";
import { useRatingWorkflow } from "../../../../context/RatingWorkflowContext";
import type { AwardedSupplier } from "newui/features/MySuppliers/types/supplier-performance.types";
import { useDebounceFunction } from "newui/hooks/useDebounceFunction";

const PROFILE_TYPE_NAMES: Record<number, string> = {
	1: "Client",
	2: "Head Contractor",
	3: "Subcontractor",
	4: "Consultant",
};

interface SelectSupplierStepProps {
	isLoading: boolean;
	suppliers: AwardedSupplier[];
	onSearchChange: (search: string) => void;
}

export const SelectSupplierStep: React.FC<SelectSupplierStepProps> = ({
	suppliers,
	isLoading,
	onSearchChange,
}) => {
	const { state, dispatch } = useRatingWorkflow();
	const [searchInput, setSearchInput] = useState("");

	const debouncedSearch = useDebounceFunction((value: string) => {
		onSearchChange(value);
	}, 300);

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchInput(e.target.value);
		debouncedSearch(e.target.value);
	};

	const handleSelect = (supplier: AwardedSupplier) => {
		dispatch({
			type: "SET_SUPPLIER",
			payload: {
				supplierId: supplier.supplier_company_id,
				supplierName: supplier.company_name,
			},
		});
	};

	useEffect(() => {
		return () => {
			debouncedSearch.cancel();
		};
	}, [debouncedSearch]);

	return (
		<div data-testid="select-supplier-step">
			<h3 className="mb-4 text-lg font-semibold text-gray-900">
				Select a Supplier
			</h3>
			<p className="mb-4 text-sm text-gray-600">
				Search for an awarded supplier to rate their performance.
			</p>

			<div className="relative mb-4">
				<FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
				<input
					type="text"
					value={searchInput}
					onChange={handleSearchChange}
					placeholder="Search by supplier name..."
					className="w-full rounded-md border border-gray-300 py-2.5 pl-10 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
					data-testid="supplier-search-input"
				/>
			</div>

			{isLoading && (
				<div className="py-8 text-center text-sm text-gray-500">
					Loading suppliers...
				</div>
			)}

			{!isLoading && suppliers.length === 0 && searchInput && (
				<div className="py-8 text-center text-sm text-gray-500">
					No suppliers found matching &quot;{searchInput}&quot;
				</div>
			)}

			{!isLoading && (
				<div className="max-h-64 space-y-2 overflow-y-auto">
					{suppliers.map((supplier) => {
						const isSelected =
							state.supplierId === supplier.supplier_company_id;
						const profileTypeName = PROFILE_TYPE_NAMES[supplier.profile_type_id] ?? "Unknown";
						return (
							<button
								key={supplier.supplier_company_id}
								type="button"
								onClick={() => handleSelect(supplier)}
								className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors ${isSelected
									? "border-blue-500 bg-blue-50"
									: "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
									}`}
								data-testid={`supplier-option-${supplier.supplier_company_id}`}
							>
								{supplier.logo_path ? (
									<img
										src={supplier.logo_path}
										alt=""
										className="h-8 w-8 rounded-full object-cover"
									/>
								) : (
									<div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-xs font-medium text-gray-600">
										{supplier.company_name
											.charAt(0)
											.toUpperCase()}
									</div>
								)}
								<div>
									<div className="text-sm font-medium text-gray-900">
										{supplier.company_name}
									</div>
									<div className="text-xs text-gray-500">
										{profileTypeName}
									</div>
								</div>
								{isSelected && (
									<div className="ml-auto text-blue-600">
										✓
									</div>
								)}
							</button>
						);
					})}
				</div>
			)}

			{state.supplierId && (
				<div
					className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3"
					data-testid="selected-supplier-preview"
				>
					<span className="text-sm font-medium text-blue-800">
						Selected: {state.supplierName}
					</span>
				</div>
			)}
		</div>
	);
};
